import {
	BuildNewOrderLineItemsHeader,
	NewOrderNumberHeader,
	StaticComponentBackground,
	StaticComponentTitle
} from "../NewOrders.styles";
import {
	EstimatedSubtotalText,
	NewOrderBuildGridDoorStyleColumnHeader,
	NewOrderBuildGridFinishColumnHeader,
	NewOrderBuildGridQuantityColumnHeader,
	NewOrderConfirmationOrderNumber,
	NewOrderConfirmationOrderPending,
	NewOrderOrderListHeader,
	NewOrderViewPriceEstimateExtendedPriceColumnHeader,
	NewOrderViewPriceEstimateListPriceColumnHeader
} from "../constants";
import { ReviewOrderListLineItemContainer, ReviewOrderListLineItems } from "./reviewOrder.styles";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { FC, useContext, useMemo } from "react";
import {
	EstimatesLineItemGrid,
	LineItemCount,
	TotalPriceContainer
} from "../ViewPriceEstimates/viewPriceEstimates.styles";
import { ColorOptionText, EditText, VendorText } from "constants/text";
import Header from "../../Common/LineItemGrid/Header/Header";
import EstimatedSubtotal from "../ViewPriceEstimates/EstimatedSubtotal";
import Modification from "components/Common/LineItemGrid/Modification/Modification";
import AccountAndBillTo from "../../Common/AccountAndBillTo/AccountAndBillTo.tsx";
import {
	ConfigurationGroup,
	formatFinishDescription,
	getAttributes,
	getConfigDisplay,
	getConfigurationGroupsByProductLine,
	getLineItemCount,
	getSubheaderAttributes
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import { Divider } from "@mui/material";
import { LinkButton } from "../../Common/Link";
import { useNavigate, useParams } from "react-router";
import { models } from "types/api/viewModels";
import { checkAcknowledgementEligibility, checkForCustomQuotePricing } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { formatPriceWithPrefix, getNewOrderURL } from "utils/string.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext";
import { OrderDetailsLineItemRow } from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemRow.tsx";
import { AcknowledgementPriceFormat } from "types/declarations/enums.ts";

interface ReviewOrderListProps {
	isConfirmationPage?: boolean;
	isAddOn?: boolean;
	isNewQuoteOrder?: boolean;
	internalQuoteDetails?: models["DraftOrderViewModel"] | models["PendingOrderViewModel"];
}

const ReviewOrderList: FC<ReviewOrderListProps> = ({
	isConfirmationPage,
	isAddOn,
	isNewQuoteOrder,
	internalQuoteDetails
}) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const validatedOrderResponse = useSelector((state: RootState) => state.newOrder.validatedOrderResponse);
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const { acknowledgementType } = checkAcknowledgementEligibility(newOrderDetails, configurationsData);
	const importedCSVData = useSelector((state: RootState) => state.newOrder.parsedCSV);
	const navigate = useNavigate();
	const params = useParams();
	const { isCustomColorPage: isCustomColor, isAddOnPage } = useUserLocation();

	const groupedDisplayConfigs = useMemo<ConfigurationGroup[] | undefined>(
		() =>
			getConfigurationGroupsByProductLine(
				internalQuoteDetails?.configurations ?? newOrderDetails?.configurations
			),
		[internalQuoteDetails?.configurations, newOrderDetails?.configurations]
	);

	const handleEdit = () => {
		navigate(getNewOrderURL(isAddOn, isCustomColor, isNewQuoteOrder, "build-order", params.orderId));
	};

	const quoteLineItemComponent: (
		config: models["PendingOrderConfigurationViewModel"] | models["ValidatedOrderConfigurationViewModel"],
		item: models["LineItemViewModel"] | models["PendingLineItemViewModel"]
	) => any = (config, item) => (
		<OrderDetailsLineItemRow
			key={`${item.sku}-${item.lineItemNumber}`}
			item={item}
			configuration={config}
			orderDetail={internalQuoteDetails ?? {}}
			variant="detail"
			isAcknowledgement={!!isNewQuoteOrder}
			ackPermission={AcknowledgementPriceFormat.NET}
			isNewQuoteOrder={!!isNewQuoteOrder}
		/>
	);

	const lineItemsComponent: (
		config: models["PendingOrderConfigurationViewModel"] | models["ValidatedOrderConfigurationViewModel"],
		item: models["LineItemViewModel"]
	) => any = (config, item) => (
		<ReviewOrderListLineItemContainer key={item?.lineItemNumber}>
			<EstimatesLineItemGrid
				mainRowContent={[
					<Header
						key={config.configurationId}
						item={item}
					/>,
					isCustomColor ? (
						<div key={`${item.sku}-${item.lineItemNumber}-vendor`}>
							<div>{VendorText}</div>
							<div data-testid="new-order-review-order-list-line-item-vendor">
								{config.globals?.finish?.customColor?.vendorDescription}
							</div>
						</div>
					) : (
						<div key={`${item.sku}-${item.lineItemNumber}-door-style`}>
							<div>{NewOrderBuildGridDoorStyleColumnHeader}</div>
							<div data-testid="new-order-review-order-list-line-item-doorStyle">
								{config.globals?.style?.description}
							</div>
						</div>
					),
					isCustomColor ? (
						<div key={`${item.sku}-${item.lineItemNumber}-colorOption`}>
							<div>{ColorOptionText}</div>
							<div data-testid="new-order-review-order-list-line-item-colorOption">
								{`${config.globals?.finish?.customColor?.colorCode} - ${config.globals?.finish?.customColor?.colorDescription}`}
							</div>
						</div>
					) : (
						<div key={`${item.sku}-${item.lineItemNumber}-finish`}>
							<div>{NewOrderBuildGridFinishColumnHeader}</div>
							<div data-testid="new-order-review-order-list-line-item-finish">
								{formatFinishDescription(config.globals?.finish)}
							</div>
						</div>
					),
					<div key={`${item.lineItemNumber}-qty`}>
						<div>{NewOrderBuildGridQuantityColumnHeader}</div>
						<div data-testid="new-order-review-order-list-line-item-quantity">{item.quantityOrdered}</div>
					</div>,
					<div key={`${String(item.lineItemNumber)}-list-price`}>
						<div>{NewOrderViewPriceEstimateListPriceColumnHeader}</div>
						<div data-testid="new-order-review-order-list-line-item-listPrice">
							{checkForCustomQuotePricing(item, item?.listPrice)}
						</div>
					</div>,
					<div key={`${String(item.lineItemNumber)}-extended-price`}>
						<div>{NewOrderViewPriceEstimateExtendedPriceColumnHeader}</div>
						<div data-testid="new-order-review-order-list-line-item-extendedPrice">
							{checkForCustomQuotePricing(item, item?.extendedPrice)}
						</div>
					</div>
				]}
				modificationRows={item.modifications?.map((modification) => (
					<Modification
						description={modification.description ?? undefined}
						key={modification.sku}
						lineNumber={modification.lineItemNumber}
						title={modification.sku ?? undefined}
						values={modification.values}
					/>
				))}
				dimensionRows={item.requiredDimensions?.map((dimension) => (
					<Modification
						dimensionDescription={dimension.description}
						key={dimension.id}
						dimensionValue={dimension.value}
						isUnrestrictedDimension={dimension.isUnrestrictedValue}
						lineNumber={null}
					/>
				))}
				requiredCategoriesRows={item.requiredCategories?.map((category) => (
					<Modification
						key={category.categoryName}
						requiredCategory={category}
						lineNumber={null}
					/>
				))}
			/>
		</ReviewOrderListLineItemContainer>
	);

	return (
		<>
			<StaticComponentBackground>
				{!isNewQuoteOrder && (
					<StaticComponentTitle data-testid="new-order-review-order-list-title">
						{NewOrderOrderListHeader}
						{!isConfirmationPage && (
							<span>
								<LinkButton
									data-testid="new-order-review-edit"
									onClick={handleEdit}
								>
									{EditText}
								</LinkButton>
							</span>
						)}
					</StaticComponentTitle>
				)}
				{groupedDisplayConfigs?.map((group, groupIndex) => (
					<div key={group.id}>
						{isConfirmationPage && !isAddOnPage && !isNewQuoteOrder && (
							<NewOrderNumberHeader>
								<div>
									<div data-testid="new-order-confirmation-order-number">
										{NewOrderConfirmationOrderNumber}
									</div>
									<div data-testid="new-order-confirmation-pending">
										&nbsp;
										{NewOrderConfirmationOrderPending}
									</div>
								</div>
							</NewOrderNumberHeader>
						)}
						<AccountAndBillTo newOrderConfig={group.configurations?.[0]} />
						<LineItemCount data-testid="new-order-build-table-count">
							{getLineItemCount(group)}
						</LineItemCount>
						{group.configurations?.map((config, configIndex) => (
							<div key={config.configurationId}>
								<BuildNewOrderLineItemsHeader>
									<div data-testid="new-order-build-label">
										<div>{getAttributes(config, isCustomColor)}</div>
										<div>{getSubheaderAttributes(config)}</div>
									</div>
								</BuildNewOrderLineItemsHeader>
								<ReviewOrderListLineItems isNewQuoteOrder={isNewQuoteOrder}>
									{config?.lineItems?.map((item) =>
										isNewQuoteOrder
											? quoteLineItemComponent(config, item)
											: lineItemsComponent(config, item)
									)}
								</ReviewOrderListLineItems>
								{getConfigDisplay(config, importedCSVData)}
								{configIndex !==
									(groupedDisplayConfigs?.[groupIndex]?.configurations?.length ?? 0) - 1 && (
									<Divider sx={{ margin: "24px 0", padding: 0 }} />
								)}
							</div>
						))}
						{!isNewQuoteOrder && (
							<TotalPriceContainer>
								<p>{EstimatedSubtotalText}</p>
								<p data-testid="new-order-review-order-list-extendedPrice-configuration">
									{formatPriceWithPrefix(
										group.configurations.reduce((accum: number, current) => {
											const price =
												"pricing" in current ? (current.pricing?.extendedPrice ?? 0) : 0;
											return accum + price;
										}, 0)
									)}
								</p>
							</TotalPriceContainer>
						)}
						{groupIndex < groupedDisplayConfigs.length - 1 && (
							<Divider sx={{ margin: "24px 0", padding: 0 }} />
						)}
					</div>
				))}
			</StaticComponentBackground>
			<EstimatedSubtotal
				data-testid="new-order-review-order-list-extendedPrice-full-order"
				pricing={
					internalQuoteDetails && "pricing" in internalQuoteDetails
						? (internalQuoteDetails.pricing?.finalNetPrice ?? internalQuoteDetails.pricing?.netPrice ?? 0)
						: (validatedOrderResponse?.pricing?.extendedNetPrice ??
							validatedOrderResponse?.pricing?.extendedPrice ??
							0)
				}
				acknowledgementType={acknowledgementType}
				isNewQuoteOrder={isNewQuoteOrder}
			/>
		</>
	);
};

export default ReviewOrderList;
